import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Table } from "@package/subframe/components/Table";
import { FeatherAlertTriangle } from "@subframe/core";
import { format, formatDistanceToNow } from "date-fns";

import { SortableHeaderCell } from "@/components/ui/sortable-header-cell";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import type { ScheduledQuestionnaireListItemDto } from "@/generated/model/scheduledQuestionnaireListItemDto";
import {
  type ChecklistManagerTab,
  type ChecklistSortColumn,
  frequencyLabel,
  primaryCategory,
  progressPercent,
  type SortDir,
  statusBadge,
} from "@/lib/checklist-manager";

import { CategoryPill, ChecklistEntityIcon } from "./category-pill";

interface ChecklistManagerTableProps {
  items: ScheduledQuestionnaireListItemDto[];
  tab: ChecklistManagerTab;
  nurseryName: string;
  activeColumn?: ChecklistSortColumn;
  activeDir: SortDir;
  onSort: (column: ChecklistSortColumn) => void;
  onView: (id: string) => void;
  /** Whether the viewer may acknowledge failed checks (NAU-71). */
  canAcknowledge?: boolean;
  /** Acknowledge a checklist straight from the Failed checks tab. */
  onAcknowledge?: (item: ScheduledQuestionnaireListItemDto) => void;
}

/** Relative-time subtitle under the checklist name (e.g. "Completed 2 hours ago"). */
function timingSubtitle(item: ScheduledQuestionnaireListItemDto): string {
  if (item.completedAt) {
    return `Completed ${formatDistanceToNow(new Date(item.completedAt), { addSuffix: true })}`;
  }
  if (item.status === "EXPIRED") {
    return `Missed ${formatDistanceToNow(new Date(item.availableUntil), { addSuffix: true })}`;
  }
  return `Due ${formatDistanceToNow(new Date(item.availableUntil), { addSuffix: true })}`;
}

function responsibleLabel(item: ScheduledQuestionnaireListItemDto): string {
  if (item.assignedTo) {
    return (
      [item.assignedTo.firstName, item.assignedTo.lastName]
        .filter(Boolean)
        .join(" ") || item.assignedTo.email
    );
  }
  const roles = item.config?.assigneeRoles ?? [];
  return roles.length > 0 ? roles.join(", ") : "—";
}

function completedByLabel(item: ScheduledQuestionnaireListItemDto): string {
  const users = item.completedBy ?? [];
  if (users.length === 0) return "—";
  return users
    .map((u) => [u.firstName, u.lastName].filter(Boolean).join(" "))
    .filter(Boolean)
    .join(", ");
}

export function ChecklistManagerTable({
  items,
  tab,
  nurseryName,
  activeColumn,
  activeDir,
  onSort,
  onView,
  canAcknowledge = false,
  onAcknowledge,
}: ChecklistManagerTableProps) {
  const isDueSoon = tab === "due-soon";
  const showAcknowledge = tab === "failed" && canAcknowledge && !!onAcknowledge;

  return (
    <Table
      className={TABLE_MIN_WIDTH.xl}
      header={
        <Table.HeaderRow>
          <SortableHeaderCell
            column="name"
            label="Checklist"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="category"
            label="Category"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <Table.HeaderCell>Location</Table.HeaderCell>
          <SortableHeaderCell
            column="frequency"
            label="Frequency"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column={isDueSoon ? "due" : "completedAt"}
            label={isDueSoon ? "Due" : "Completed"}
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <Table.HeaderCell>
            {isDueSoon ? "Responsible" : "Completed by"}
          </Table.HeaderCell>
          <SortableHeaderCell
            column="progress"
            label="Progress"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <SortableHeaderCell
            column="status"
            label="Status"
            activeColumn={activeColumn}
            activeDir={activeDir}
            onSort={onSort}
          />
          <Table.HeaderCell variant="aligned-right">Actions</Table.HeaderCell>
        </Table.HeaderRow>
      }
    >
      {items.map((item) => {
        const category = primaryCategory(item);
        const badge = statusBadge(item.status);
        const progress = progressPercent(item);
        const dueOrCompleted = isDueSoon
          ? new Date(item.availableUntil)
          : item.completedAt
            ? new Date(item.completedAt)
            : null;

        return (
          <Table.Row key={item.id} clickable onClick={() => onView(item.id)}>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex items-center gap-3">
                <ChecklistEntityIcon
                  slug={category?.slug}
                  color={category?.color}
                />
                <div className="flex flex-col items-start">
                  <span className="text-body-bold font-body-bold text-neutral-900">
                    {item.template?.name}
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    {timingSubtitle(item)}
                  </span>
                </div>
              </div>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              {category ? (
                <CategoryPill
                  name={category.name}
                  slug={category.slug}
                  color={category.color}
                />
              ) : (
                <span className="text-caption font-caption text-subtext-color">
                  —
                </span>
              )}
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {nurseryName}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {frequencyLabel(item.config?.frequency)}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              {dueOrCompleted ? (
                <div className="flex flex-col items-start">
                  <span className="text-caption font-caption text-neutral-900">
                    {format(dueOrCompleted, "d MMM yyyy")}
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    {format(dueOrCompleted, "HH:mm")}
                  </span>
                </div>
              ) : (
                <span className="text-caption font-caption text-subtext-color">
                  —
                </span>
              )}
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <span className="text-caption font-caption text-neutral-900">
                {isDueSoon ? responsibleLabel(item) : completedByLabel(item)}
              </span>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-16 overflow-hidden rounded-full bg-neutral-100">
                  <div
                    className="h-full rounded-full bg-primary-navy-600"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-caption font-caption text-subtext-color">
                  {progress}%
                </span>
              </div>
            </Table.Cell>
            <Table.Cell className="h-14 grow shrink-0 basis-0">
              <div className="flex items-center gap-1.5">
                <Badge variant={badge.variant}>{badge.label}</Badge>
                {item.failedCheckCount > 0 && (
                  <Badge variant="error" icon={<FeatherAlertTriangle />}>
                    {String(item.failedCheckCount)}
                  </Badge>
                )}
              </div>
            </Table.Cell>
            <Table.Cell
              className="h-14 grow shrink-0 basis-0"
              variant="aligned-right"
            >
              <div
                className="flex items-center justify-end gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                {showAcknowledge && item.hasUnresolvedFailedCheck && (
                  <Button
                    variant="brand-secondary"
                    onClick={() => onAcknowledge?.(item)}
                  >
                    Acknowledge
                  </Button>
                )}
                <Button
                  variant="neutral-primary"
                  onClick={() => onView(item.id)}
                >
                  View
                </Button>
              </div>
            </Table.Cell>
          </Table.Row>
        );
      })}
    </Table>
  );
}
